import {put, call, all} from 'redux-saga/effects';
import MatterStage from "../../entities/matterStage";
import * as Actions from "../actions/matterStageActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";
import {ensureOperationId} from '../../lib/pendingRows';
import {tabSession} from '../../Auth/session';

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type MatterStageAction = {
    matterStageId: string;
    matterStage: MatterStage;
    matterStages: MatterStage[];
    sentAddedOn?: number;
    onQueued?: (saved: boolean) => void;
};

export function* fetchOneMatterStage(action: MatterStageAction) {
	const session = tabSession.captureGeneration();
	try {
		const matterStage: MatterStage = yield call(api.findOne, 'matterStage', action.matterStageId);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.MATTERSTAGE_FETCH_ONE_SUCCESS, matterStageId: action.matterStageId, matterStage, sentAddedOn: action.sentAddedOn});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.MATTERSTAGE_FETCH_ONE_FAILURE, matterStageId: action.matterStageId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {MatterStageAction} action
 */
export function* saveMatterStage(action: MatterStageAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ MATTERSTAGE_SAVE saga', action.matterStage?.id);

		if (action.matterStage === undefined || action.matterStage === null) {
			console.error('saveMatterStage received a null');
			throw new Error('saveMatterStage received a null');
		}

		if (!(action.matterStage instanceof MatterStage)) {
			action.matterStage = new MatterStage(action.matterStage);
		}
		// OWA-1018: the reducer showed this save at dispatch, stamped after the version this tab holds
		// (OWA-1083), marked pending under this queue key
		const dispatchedAddedOn = action.matterStage.addedOn;

		try {
			// Save to IndexedDB
			ensureOperationId(action.matterStage);
			const addResult = yield call(idb.add, 'matterStage', action.matterStage);
			if (addResult.isErr()) {
				console.error(`⮂ saveMatterStage: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The save never reached the queue: its row returns to the latest accepted version
				yield put(Actions.revertMatterStageSave(action.matterStage, {sentAddedOn: dispatchedAddedOn}));
				yield put({type: Actions.MATTERSTAGE_SAVE_FAILURE, matterStage: action.matterStage, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copy replaces the row shown at dispatch
			// (the queue may have stamped it again)
			yield put({type: Actions.MATTERSTAGE_SAVE, matterStage: action.matterStage, dispatchedAddedOn, queuedAt: performance.now()});

			// A save never deletes autosaved drafts (OWA-958). The editor that wrote a draft
			// removes it when it saves; a draft typed somewhere else may be newer than this copy.
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue);
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: a save that never reached the queue returns its row to the latest accepted version
			if (!queueReported) yield put(Actions.revertMatterStageSave(action.matterStage, {sentAddedOn: dispatchedAddedOn}));
			reportQueued(false);
			yield put({type: Actions.MATTERSTAGE_SAVE_FAILURE, matterStage: action.matterStage, status: 500, err});

			console.error('Error saving to database', err);
		}
    } finally { reportQueued(false); }
}

/**
 * TODO: Update for saveMany
 * @param {MatterStageAction} action
 * @param {MatterStage[]} action.matterStages
 */
export function* saveManyMatterStages(action: MatterStageAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ MATTERSTAGE_SAVE_MANY saga', action.matterStages.map(matterStage => matterStage.id).join(','));

        if (!Array.isArray(action.matterStages)) {
            console.error('saveManyMatterStages expected an array, received', action.matterStages);
            throw new Error('saveManyMatterStages expected an array');
        }
		// OWA-1018: the reducer showed these saves at dispatch (see saveMatterStage), under these queue keys
		const dispatchedAddedOns: number[] = action.matterStages.map(matterStage => matterStage?.addedOn);
		const revertAll = () => action.matterStages.map((matterStage, i) =>
			put(Actions.revertMatterStageSave(matterStage, {sentAddedOn: dispatchedAddedOns[i]})));

		try {
			const toSave: MatterStage[] = [];

			// Save each item to IndexedDB
			for(let i = 0; i < action.matterStages.length; i++) {
				const matterStage = action.matterStages[i];
				matterStage.persisted = false;
				ensureOperationId(matterStage);
				toSave.push(matterStage);
			}

			const addResult = yield call(idb.add, 'matterStage', toSave);
			if (addResult.isErr()) {
				console.error(`⮂ saveManyMatterStages: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The saves never reached the queue: their rows return to the latest accepted versions
				yield all(revertAll());
				yield put({type: Actions.MATTERSTAGE_SAVE_MANY_FAILURE, matterStages: action.matterStages, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copies replace the rows shown at dispatch
			yield put({type: Actions.MATTERSTAGE_SAVE_MANY, matterStages: toSave, dispatchedAddedOns, queuedAt: performance.now()});
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue)
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: saves that never reached the queue return their rows to the latest accepted versions
			if (!queueReported) yield all(revertAll());
			reportQueued(false);
			yield put({type: Actions.MATTERSTAGE_SAVE_MANY_FAILURE, matterStages: action.matterStages, status: 500, err});

			console.error(`Error saving ${action.matterStages.length} matterStages to database`, err);
		}
    } finally { reportQueued(false); }
}

/**
 * @param {MatterStageAction} action
 */
export function* removeMatterStage(action: MatterStageAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
	if (!userId || action.matterStage === undefined || action.matterStage === null || !(action.matterStage instanceof MatterStage)) {
			console.error('removeMatterStage expected type MatterStage, received', action.matterStage);
			reportQueued(false);
			throw new Error('removeMatterStage expected type MatterStage');
		}

	try {
		// Mark as deleted. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.matterStage.status = 'D';
		action.matterStage.addedOn = newHRTimestamp();
		action.matterStage.addedBy = userId;

		// Save to IndexedDB
		ensureOperationId(action.matterStage);
		const addResult = yield call(idb.add, 'matterStage', action.matterStage);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ removeMatterStage: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			yield put({type: Actions.MATTERSTAGE_REMOVE_FAILURE, matterStage: action.matterStage, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.MATTERSTAGE_REMOVE, matterStage: action.matterStage});
		session.assertCurrent();

        reportQueued(true);

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.MATTERSTAGE_REMOVE_FAILURE, matterStage: action.matterStage, status: 500, err});

		console.error('Error deleting from database', err);
	}
    finally { reportQueued(false); }
}

/**
	* Restore the last version of this MatterStage.
	*
	* @param {MatterStageAction} action
	*/
export function* restoreMatterStage(action: MatterStageAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreMatterStage expected getCurrentUserId() to return a value');
        return;
    }

	if (action.matterStage === undefined || action.matterStage === null) {
        console.error('restoreMatterStage expected a MatterStage, received null');
        throw new Error('restoreMatterStage expected a MatterStage, received null');
    }
    else if (!(action.matterStage instanceof MatterStage)) action.matterStage = new MatterStage(action.matterStage); 

	try {
		// Mark as restored. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.matterStage.status = 'U';
		action.matterStage.addedOn = newHRTimestamp();
		action.matterStage.addedBy = userId;
		action.matterStage.restore = true;

		// Save to IndexedDB
		ensureOperationId(action.matterStage);
		const addResult = yield call(idb.add, 'matterStage', action.matterStage);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ restoreMatterStage: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.MATTERSTAGE_RESTORE_FAILURE, matterStage: action.matterStage, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.MATTERSTAGE_RESTORE, matterStage: action.matterStage});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.MATTERSTAGE_RESTORE_FAILURE, matterStage: action.matterStage, status: 500, err});

		console.error('Error restoring from database', err);
	}
}